/// <amd-module name="Repos/Common/Util/Date" />
import { format } from "VSS/Core/Util/String";
import { ago } from "VSSUI/Utilities/Date";

/**
 * Returns true if difference between @param date and the current time is less than 24 hours. Returns false otherwise.
 * @param date
 */
export function isInLast24Hours(date: Date): boolean {
    if (!date) {
        return false;
    }

    const oneHourInMilliseconds: number = 60 * 60 * 1000;
    const dateNow: Date = new Date();

    return Math.abs(dateNow.getTime() - date.getTime()) / oneHourInMilliseconds < 24;
}

/**
 * Returns the appropriate string representation of the date given along with friendly text, if applicable.
 * If the date given is recent, this returns a string such as "2/29/2016 5.45 PM (7 hours ago)".
 * Otherwise, this returns a string such as "2/29/2016 5:45 PM" according to the format if specified.
 */
export function getDateStringWithFriendlyText(date: Date): string | undefined {
    if (!date) {
        return undefined;
    }

    const isCreationDateRecent: boolean = isInLast24Hours(date);
    const dateString: string = date.toLocaleString();

    if (isCreationDateRecent) {
        return format("{0} ({1})", dateString, ago(date));
    }

    return dateString;
}

export function convertToEndOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

export function convertToUtcEndOfDay(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
}

export function convertToUtcStartOfDay(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
}

/*
    passed in string will look like 2019-08-12T00:00:00.000Z.  This will return a date that applies
    local time zone offset.  Returned date will be Mon Aug 12 2019 00:00:00 GMT-0400.
*/
export function applyLocalTimeZoneOffset(dateString: string): Date {
    const toDate = new Date(dateString);
    const localTime = toDate.getTime();
    const localOffset = toDate.getTimezoneOffset() * 60000;
    const newTime = localTime + localOffset;
    return new Date(newTime);
}
